import { clearAllCanvasNavigationContexts } from '../navigation/canvasNavigationContext'
import { clearAllCachedBackofficeResources } from '../workspace/backofficeResourceCache'
import { invalidateSingleFlightCache } from '../workspace/contentProduction/canvas/canvasRequestDedup'
import { invalidateAuthMatrixCache } from '../workspace/contentProduction/canvas/authMatrixRuntime'
import { invalidateNodeCatalogCache } from '../workspace/contentProduction/canvas/nodeCatalogRuntime'
import { invalidateQuickActionCache } from '../workspace/contentProduction/canvas/canvasQuickActionRuntime'
import { resolvePostSwitchRoute, type SwitchTargetMe } from './accountSwitchRoute'
import { invalidateDeviceAccounts } from './deviceAccountsStore'

export const ACCOUNT_SWITCH_STORAGE_KEY = 'vistoera:accountSwitchTick'

/** Clears client state that must not leak across identities (in-memory caches
 * die with the page reload anyway; sessionStorage Canvas return contexts and
 * the device-account list are cleared explicitly). */
export function clearCrossAccountClientState(): void {
  clearAllCachedBackofficeResources()
  invalidateSingleFlightCache()
  invalidateDeviceAccounts()
  // Canvas "return to Canvas" context holds the old identity's project id.
  clearAllCanvasNavigationContexts()
  // VistoeraTASK-00583 — never reuse another account's creatable-node set.
  invalidateNodeCatalogCache()
  // VistoeraTASK-00634 — never reuse another account's effective auth matrix.
  invalidateAuthMatrixCache()
  // TASK-00584 - same isolation for the Admin Quick Action snapshot.
  invalidateQuickActionCache()
}

export type SessionResyncAction = 'keep' | 'reload_as_session_user' | 'to_login'

/**
 * VistoeraTASK-00573 — what a tab does once it learns the shared browser
 * session may have changed (another tab's login/logout/switch, or a 409
 * `session_identity_changed`). Pure. `renderedUserId` is the identity this
 * tab currently renders (`null` = logged-out UI); `sessionUserId` is what
 * `GET /me` answered (`null` = definitively unauthenticated).
 */
export function decideSessionResync(
  renderedUserId: string | null,
  sessionUserId: string | null,
): SessionResyncAction {
  if (sessionUserId === renderedUserId) return 'keep'
  return sessionUserId === null ? 'to_login' : 'reload_as_session_user'
}

export function notifyOtherTabsAccountSwitched(): void {
  try {
    localStorage.setItem(ACCOUNT_SWITCH_STORAGE_KEY, String(Date.now()))
  } catch {
    // Private mode / blocked storage — other tabs won't auto-sync.
  }
}

/**
 * Once the browser's session cookie has been switched on the server, a
 * `beforeunload` "leave site?" prompt from an unrelated page guard must not be
 * able to keep the OLD UI alive on top of the NEW identity (that mix is what
 * produced the false 403). Capture-phase + stopImmediatePropagation runs
 * before any page-registered handler.
 */
export function suppressUnloadPromptsForAccountSwitch(): void {
  window.addEventListener('beforeunload', (event) => event.stopImmediatePropagation(), { capture: true })
}

export interface AccountSwitchDeps {
  /** `POST /auth/switch-account` — must NOT touch React auth state. */
  callSwitchApi: (userId: string) => Promise<SwitchTargetMe>
  clearState: () => void
  notifyOtherTabs: () => void
  suppressUnloadPrompts: () => void
  /** Full document navigation (`location.replace`), never an SPA navigate. */
  hardNavigate: (url: string) => void
}

/**
 * VistoeraTASK-00563 — controlled account switch.
 *
 * 1. Server switch first. If it fails nothing is cleared and nothing navigates,
 *    so the original login/page stays exactly as it was.
 * 2. Only after the new session cookie is live: clear old-identity state and
 *    tell other tabs.
 * 3. Map the pre-switch path onto the new account and do ONE full page load.
 *    The normal boot path (`GET /me` → workspace load → route guards) then runs
 *    with the new session only, so the old workspace id can never be requested
 *    with the new identity and React never renders "new me + old URL".
 *
 * Returns the URL navigated to.
 */
export async function executeAccountSwitch(
  userId: string,
  fromPathname: string,
  deps: AccountSwitchDeps,
): Promise<string> {
  const target = await deps.callSwitchApi(userId)
  deps.clearState()
  deps.notifyOtherTabs()
  const url = resolvePostSwitchRoute(fromPathname, target)
  deps.suppressUnloadPrompts()
  deps.hardNavigate(url)
  return url
}
